/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetIpReputationData } from "@/hooks/use-postmaster";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { type ColDef } from "ag-grid-community";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// Sample data - replace with actual Postmaster data
const ipReputationData = [
  { date: "Oct 15", high: 100, medium: 0, low: 0, bad: 0 },
  { date: "Oct 16", high: 100, medium: 0, low: 0, bad: 0 },
  { date: "Oct 17", high: 100, medium: 0, low: 0, bad: 0 },
  { date: "Oct 18", high: 100, medium: 0, low: 0, bad: 0 },
  { date: "Oct 19", high: 100, medium: 0, low: 0, bad: 0 },
  { date: "Oct 20", high: 100, medium: 0, low: 0, bad: 0 },
];

const highReputationIPs = [
  { ip: "50.114.3.14", status: "High" },
  { ip: "50.114.3.22", status: "High" },
  { ip: "50.114.3.38", status: "High" },
];

function IpTabContent({ date, domain }: { date: string; domain: string }) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedDayIps, setSelectedDayIps] = useState<
    { ip: string; status: string }[]
  >([]);

  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (date) {
      case "last7days":
        start.setDate(end.getDate() - 7);
        break;
      case "last30days":
        start.setDate(end.getDate() - 30);
        break;
      case "last60days":
        start.setDate(end.getDate() - 60);
        break;
      case "last90days":
        start.setDate(end.getDate() - 90);
        break;
      case "last120days":
        start.setDate(end.getDate() - 120);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }

    return { startDate: start, endDate: end };
  }, [date]);

  const { data, isLoading } = useGetIpReputationData({
    domain,
    startDate,
    endDate,
  });

  const columnDefs: ColDef[] = [
    {
      field: "ip",
      headerName: "IP Address",
      filter: true,
      sortable: true,
      cellClass: "font-mono",
    },
    {
      field: "status",
      headerName: "Status",
      filter: true,
      sortable: true,
      cellRenderer: (params: any) => {
        const colorMap: Record<string, string> = {
          HIGH: "bg-emerald-500",
          MEDIUM: "bg-yellow-500",
          LOW: "bg-red-500",
          BAD: "bg-red-900",
        };
        const status = params.value as keyof typeof colorMap;
        return (
          <Badge className={colorMap[status] || "bg-gray-500"}>
            {params.value}
          </Badge>
        );
      },
    },
    {
      field: "date",
      headerName: "Last Updated",
      filter: true,
      sortable: true,
    },
  ];

  const formattedChartData = data?.chartData.map((item) => ({
    ...item,
    date: format(new Date(item.date), "MMM dd, yyyy"),
  }));

  // Get and sort IPs for selected date
  const getIpsForDate = (date: string) => {
    const dayData = data?.chartData.find(
      (item) => format(new Date(item.date), "MMM dd") === date
    );
    if (!dayData) return [];

    // Sort IPs by reputation priority: HIGH -> MEDIUM -> LOW -> BAD
    const reputationPriority = { HIGH: 0, MEDIUM: 1, LOW: 2, BAD: 3 };

    const allIps = dayData.ipReputations.flatMap((rep) =>
      (rep.sampleIps || []).map((ip) => ({
        ip,
        status: rep.reputation,
      }))
    );

    return allIps.sort(
      (a, b) =>
        reputationPriority[a.status as keyof typeof reputationPriority] -
        reputationPriority[b.status as keyof typeof reputationPriority]
    );
  };

  // Add this function to get all IPs from the chart data
  const getAllIps = useMemo(() => {
    if (!data?.chartData) return [];

    // Flatten all IP reputations from all dates and include the date
    const allIps = data.chartData.flatMap((day) =>
      day.ipReputations.flatMap((rep) =>
        (rep.sampleIps || []).map((ip) => ({
          ip,
          status: rep.reputation,
          date: format(new Date(day.date), "MMM dd, yyyy"), // Include formatted date
        }))
      )
    );

    // Remove duplicates by IP, keeping the most recent status and date
    const uniqueIps = Array.from(
      allIps.reduce((map, item) => map.set(item.ip, item), new Map()).values()
    );

    // Sort IPs by reputation priority: HIGH -> MEDIUM -> LOW -> BAD
    const reputationPriority = { HIGH: 0, MEDIUM: 1, LOW: 2, BAD: 3 };
    return uniqueIps.sort(
      (a, b) =>
        reputationPriority[a.status as keyof typeof reputationPriority] -
        reputationPriority[b.status as keyof typeof reputationPriority]
    );
  }, [data?.chartData]);

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-[50vh] w-full text-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Current IP Reputation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-500">High</Badge>
              <span className="text-2xl font-bold">
                {data?.statistics.currentReputation.toFixed(1)}%
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {data?.statistics.currentReputation === 100
                ? "All IPs in good standing"
                : "Some IPs need attention"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Active IPs
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold">
              {data?.statistics.totalActiveIps}
            </div>
            <p className="text-xs text-muted-foreground">Currently monitored</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Monitoring Period
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold">
              {data?.statistics.monitoringPeriod}
            </div>
            <p className="text-xs text-muted-foreground">Rolling window</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div
              className={`text-sm font-medium ${
                data?.statistics.status === "Excellent"
                  ? "text-green-600"
                  : "text-yellow-600"
              }`}
            >
              {data?.statistics.status}
            </div>
            <p className="text-xs text-muted-foreground">
              {data?.statistics.status === "Excellent"
                ? "Consistent high reputation"
                : "Improvement needed"}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="p-4">
          <CardTitle>IP Reputation Trend</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={formattedChartData}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                onClick={(data) => {
                  if (data.activePayload) {
                    const clickedDate = data.activePayload[0].payload.date;
                    setSelectedDate(clickedDate);
                    setSelectedDayIps(getIpsForDate(clickedDate));
                  }
                }}
              >
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                  formatter={(value: number, name: string) => [
                    value,
                    name === "high"
                      ? "High"
                      : name === "medium"
                      ? "Medium"
                      : name === "low"
                      ? "Low"
                      : "Bad",
                  ]}
                  labelFormatter={(label) => label}
                />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                  // interval="preserveStartEnd"
                  // angle={-45}
                  // textAnchor="end"
                  // height={60}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                />
                <Bar
                  dataKey="bad"
                  stackId="reputation"
                  fill="#991B1B"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="low"
                  stackId="reputation"
                  fill="#EF4444"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="medium"
                  stackId="reputation"
                  fill="#F59E0B"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="high"
                  stackId="reputation"
                  fill="#10B981"
                  radius={[0, 0, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 flex items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm bg-[#991B1B]" />
              <span className="text-sm">Bad</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm bg-[#EF4444]" />
              <span className="text-sm">Low</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm bg-[#F59E0B]" />
              <span className="text-sm">Medium</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm bg-[#10B981]" />
              <span className="text-sm">High</span>
            </div>
          </div>

          {/* {selectedDate && ( */}
          {/* <div className="mt-6">
              <h3 className="mb-4 text-sm font-medium">
                IP Reputation for {selectedDate}
              </h3>
              <DataTable
                rowData={selectedDayIps}
                columnDefs={columnDefs}
                height={400}
              />
            </div> */}
          {/* )} */}
        </CardContent>
      </Card>

      <DataTable rowData={getAllIps} columnDefs={columnDefs} height={400} />
    </div>
  );
}

// Export both the component for use in other files and the Route for the router
export { IpTabContent };

function IpTabRouteComponent() {
  // Get domain and date from search params if available, otherwise use defaults
  const search = useSearch({ strict: false });
  const domain = (search as { domain?: string })?.domain || "";
  const date = (search as { date?: string })?.date || "last7days";
  return <IpTabContent date={date} domain={domain} />;
}

export const Route = createFileRoute("/_private/postmaster/tabs/ip-tab")({
  component: IpTabRouteComponent,
});
